import { useState, useEffect } from "react";
import { RefreshCw } from "lucide-react";

/**
 * Friendly "Synced just now" / "Synced 2m ago" pill with a built-in refresh
 * affordance. Drop in to any page header.
 *
 * Props:
 *   - lastSyncedAt: number | null  — millis epoch when data last loaded
 *   - loading: boolean              — show spinner + disable click
 *   - onRefresh: () => void         — handler for click-to-refetch
 *   - testId?: string               — data-testid prefix (default 'sync-pill')
 *   - live?: boolean                — render a pulsing "Live" dot (used to
 *                                     signal that the page is auto-refreshing)
 */
export function SyncPill({ lastSyncedAt, loading, onRefresh, testId = "sync-pill", live = false }) {
  // Tick every 30s so the relative timestamp stays accurate without a refetch.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const relative = formatRelative(lastSyncedAt);
  const titleText = lastSyncedAt
    ? `Last synced ${new Date(lastSyncedAt).toLocaleTimeString()}${live ? " · auto-refreshing every 60s" : ""}`
    : "Click to sync";

  return (
    <button
      type="button"
      onClick={onRefresh}
      disabled={loading}
      title={titleText}
      data-testid={testId}
      className="group flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20 hover:text-emerald-200 transition-colors disabled:opacity-60 disabled:cursor-wait"
    >
      {live && (
        <span
          className="relative flex h-2 w-2 shrink-0"
          aria-hidden="true"
          data-testid={`${testId}-live-dot`}
        >
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
        </span>
      )}
      <RefreshCw
        className={`w-3.5 h-3.5 ${loading ? "animate-spin" : "group-hover:rotate-180 transition-transform duration-500"}`}
      />
      <span className="text-xs font-medium">
        {loading ? (
          "Syncing…"
        ) : lastSyncedAt ? (
          <>
            {live && <span className="mr-1 text-emerald-400">Live ·</span>}
            Synced <span data-testid={`${testId}-relative`}>{relative}</span>
          </>
        ) : (
          "Sync now"
        )}
      </span>
    </button>
  );
}

function formatRelative(ts) {
  if (!ts) return "";
  const seconds = Math.floor((Date.now() - ts) / 1000);
  if (seconds < 5) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}
